import type { Webcam } from '../core/webcams';

export interface WebcamPanelProps {
  webcam: Webcam;
  onClose: () => void;
}

/**
 * What a webcam marker opens.
 *
 * Separate from FeaturePanel on purpose. That panel explains *charted* objects
 * in plain English and everything in it carries survey provenance; a webcam
 * carries none, and putting the two behind the same panel would blur exactly
 * the distinction that matters. This one says where the position came from and
 * sends the skipper to someone else's website.
 *
 * The link opens in a new tab with `noopener` — the app must survive whatever
 * the camera's page does, and on a boat losing the chart to a third-party site
 * is a real cost.
 */
export function WebcamPanel({ webcam, onClose }: WebcamPanelProps): React.JSX.Element {
  return (
    <div className="sheet sheet--webcam" role="dialog" aria-label={`Webcam: ${webcam.name}`}>
      <div className="sheet__header">
        <h2>{webcam.name}</h2>
        <button type="button" onClick={onClose} aria-label="Close">✕</button>
      </div>

      {webcam.location && <p className="sheet__position">{webcam.location}</p>}

      <a
        className="webcam__open"
        href={webcam.url}
        target="_blank"
        rel="noopener noreferrer"
      >
        Open webcam
      </a>

      <p className="sheet__hint sheet__hint--small">
        Opens {new URL(webcam.url).host} in a new tab, and needs a connection —
        downloaded charts work offline, webcams do not.
      </p>

      <p className="sheet__hint sheet__hint--small">
        Not chart data. The marker is where {webcam.operator} says the camera is,
        not a surveyed position, and it shows where the camera stands rather than
        what it looks at.
      </p>
    </div>
  );
}
